import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHelpBot } from '../hooks/useHelpBot';
import { Alert, Tooltip } from './ComponentLibrary';
import agentAvatar from '../assets/helpbot/agent-avatar.png';
import agentAvatarFace from '../assets/helpbot/agent-avatar-face.png';
import './HelpBotWidget.css';

const SUGGESTED_QUESTIONS = [
  'How do I list a property?',
  'How does messaging work?',
  'What are the safety tips?',
  'How do I use NaijaNest?',
];

// Below this, the near-full-screen chat sheet; at/above it, the centered
// dialog. Deliberately narrower than the site-wide 900px mobile-nav
// breakpoint - a full-screen takeover of the chat only makes sense once the
// screen itself is phone-narrow, not merely tablet-narrow.
const MOBILE_BREAKPOINT = 480;

// Discovery teaser (pulse + speech-bubble hint) shown once to nudge
// visitors toward the launcher - never a marketing popup, never blocking.
// Tracked in sessionStorage so ordinary SPA navigation within the same tab
// (which never unmounts this component) doesn't re-trigger it, but an
// actual page reload clears that flag first, so refreshing shows it again
// rather than remembering "seen" forever across the tab's life.
const TEASER_STORAGE_KEY = 'naijanest_helpbot_teaser_seen';
const TEASER_SHOW_DELAY_MS = 3000;
const TEASER_VISIBLE_MS = 30000;
const TEASER_MESSAGE = 'Need a hand? Ask NaijaNest.';

function isPageReload() {
  try {
    const [entry] = performance.getEntriesByType('navigation');
    return entry ? entry.type === 'reload' : performance.navigation?.type === 1;
  } catch {
    return false;
  }
}

function hasSeenTeaser() {
  try {
    if (isPageReload()) {
      sessionStorage.removeItem(TEASER_STORAGE_KEY);
      return false;
    }
    return sessionStorage.getItem(TEASER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markTeaserSeen() {
  try {
    sessionStorage.setItem(TEASER_STORAGE_KEY, '1');
  } catch {
    // sessionStorage unavailable - the teaser may just show again next
    // visit, non-critical.
  }
}

// Splits AI reply text into React nodes without ever touching
// dangerouslySetInnerHTML - AI output is never trusted as HTML. Supports
// just enough structure to read naturally: paragraphs, line breaks,
// "- "/numbered bullet lists, and **bold** spans.
function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, i) => {
    const match = part.match(/^\*\*([^*]+)\*\*$/);
    return match ? <strong key={`${keyPrefix}-${i}`}>{match[1]}</strong> : <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

function isBulletLine(line) {
  return /^(-|\d+\.)\s+/.test(line.trim());
}

function formatMessageText(text) {
  const paragraphs = String(text || '').split(/\n{2,}/);
  return paragraphs.map((paragraph, pIndex) => {
    const lines = paragraph.split('\n').filter((l) => l.trim());
    if (lines.length > 1 && lines.every(isBulletLine)) {
      const ordered = /^\d+\./.test(lines[0].trim());
      const ListTag = ordered ? 'ol' : 'ul';
      return (
        <ListTag key={pIndex} className="helpbot-list">
          {lines.map((line, lIndex) => (
            <li key={lIndex}>{renderInline(line.replace(/^(-|\d+\.)\s+/, ''), `${pIndex}-${lIndex}`)}</li>
          ))}
        </ListTag>
      );
    }
    return (
      <p key={pIndex}>
        {lines.map((line, lIndex) => (
          <span key={lIndex}>
            {renderInline(line, `${pIndex}-${lIndex}`)}
            {lIndex < lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>
    );
  });
}

// The launcher shows the full-body photo (bigger, uncropped, no circle -
// see .helpbot-launcher in the CSS); every smaller "who you're talking to"
// spot (teaser, chat header, message rows) uses the face-forward crop,
// which frames much better as a small circle.
function AgentAvatar({ full = false }) {
  return <img src={full ? agentAvatar : agentAvatarFace} alt="" className="helpbot-avatar-img" />;
}

function MessageBubble({ message, onActionClick }) {
  const isUser = message.role === 'user';
  return (
    <div className={`helpbot-row helpbot-row--${isUser ? 'user' : 'bot'}`}>
      {!isUser && (
        <div className="helpbot-avatar" aria-hidden="true">
          <AgentAvatar />
        </div>
      )}
      <div className="helpbot-col">
        <div className={`helpbot-bubble helpbot-bubble--${isUser ? 'user' : 'bot'}`}>
          {isUser ? message.content : formatMessageText(message.content)}
        </div>
        {!isUser && Array.isArray(message.actions) && message.actions.length > 0 && (
          <div className="helpbot-actions">
            {message.actions.map((action) => (
              <Link
                key={action.id}
                to={action.route}
                className="helpbot-action-btn"
                onClick={() => onActionClick && onActionClick()}
              >
                {action.label}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="helpbot-row helpbot-row--bot" aria-hidden="true">
      <div className="helpbot-avatar">
        <AgentAvatar />
      </div>
      <div className="helpbot-typing">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export default function HelpBotWidget({ disabled = false }) {
  const bot = useHelpBot();
  const [draft, setDraft] = useState('');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches,
  );
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const [showTeaser, setShowTeaser] = useState(false);

  // Discovery teaser: waits TEASER_SHOW_DELAY_MS after the launcher first
  // mounts unopened, shows once, auto-hides after TEASER_VISIBLE_MS, and
  // won't re-trigger from ordinary SPA navigation (disabled toggling on/off
  // as the user visits auth pages, say) within the same tab - but a real
  // page reload clears that memory, so refreshing shows it again.
  useEffect(() => {
    if (disabled || bot.isOpen || hasSeenTeaser()) {
      return undefined;
    }
    const showTimer = setTimeout(() => {
      setShowTeaser(true);
      markTeaserSeen();
    }, TEASER_SHOW_DELAY_MS);
    return () => clearTimeout(showTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => {
    if (!showTeaser) {
      return undefined;
    }
    const hideTimer = setTimeout(() => setShowTeaser(false), TEASER_VISIBLE_MS);
    return () => clearTimeout(hideTimer);
  }, [showTeaser]);

  // matchMedia's own "change" event, not window.resize - some browsers
  // (Chrome DevTools' device toolbar in particular) don't reliably fire a
  // resize event on every viewport change, which left this stuck showing
  // the desktop dialog at phone widths if the panel had first mounted wide.
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const onChange = (event) => setIsMobile(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Keep the composer above the on-screen keyboard on mobile - no
  // precedent for this in the app yet (checked), visualViewport is the
  // standard way to detect keyboard-driven viewport shrink. Feature-
  // detected: does nothing where unsupported (older Safari), which just
  // means the input falls back to normal fixed positioning.
  useEffect(() => {
    if (!isMobile || !bot.isOpen || !window.visualViewport || !panelRef.current) {
      return undefined;
    }
    const vv = window.visualViewport;
    const panel = panelRef.current;
    const onViewportResize = () => {
      const keyboardInset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      panel.style.setProperty('--helpbot-keyboard-inset', `${keyboardInset}px`);
    };
    vv.addEventListener('resize', onViewportResize);
    vv.addEventListener('scroll', onViewportResize);
    onViewportResize();
    return () => {
      vv.removeEventListener('resize', onViewportResize);
      vv.removeEventListener('scroll', onViewportResize);
      panel.style.removeProperty('--helpbot-keyboard-inset');
    };
  }, [isMobile, bot.isOpen]);

  useEffect(() => {
    if (bot.isOpen && !bot.isMinimized) {
      inputRef.current?.focus();
    }
  }, [bot.isOpen, bot.isMinimized]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [bot.messages, bot.isLoading, bot.error]);

  useEffect(() => {
    if (!bot.isOpen) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        bot.close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bot.isOpen]);

  const hasMessages = bot.messages.length > 0;

  const submitDraft = useMemo(
    () => () => {
      if (!draft.trim()) {
        return;
      }
      bot.sendMessage(draft);
      setDraft('');
    },
    [draft, bot],
  );

  if (disabled) {
    return null;
  }

  if (!bot.isOpen) {
    const openFromTeaser = () => {
      setShowTeaser(false);
      bot.open();
    };

    return (
      <div className="helpbot-launcher-wrap">
        {showTeaser && (
          <div className="helpbot-teaser" role="status">
            <button type="button" className="helpbot-teaser__body" onClick={openFromTeaser}>
              <span className="helpbot-teaser__avatar" aria-hidden="true">
                <AgentAvatar />
              </span>
              {TEASER_MESSAGE}
            </button>
            <button
              type="button"
              className="helpbot-teaser__dismiss"
              onClick={() => setShowTeaser(false)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        <button
          type="button"
          className={`helpbot-launcher${showTeaser ? ' helpbot-launcher--pulse' : ''}`}
          onClick={bot.open}
          aria-label="Open Help Bot"
        >
          <AgentAvatar full />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`helpbot-panel${isMobile ? ' helpbot-panel--mobile' : ''}${bot.isMinimized ? ' helpbot-panel--minimized' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-label="NaijaNest Help Bot"
      ref={panelRef}
    >
      <div className="helpbot-header">
        {isMobile && (
          <button type="button" className="helpbot-icon-btn" onClick={bot.close} aria-label="Close Help Bot">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
        <div className="helpbot-header__avatar" aria-hidden="true">
          <AgentAvatar />
        </div>
        <div className="helpbot-header__titles">
          <div className="helpbot-header__title">NaijaNest Help</div>
          <div className="helpbot-header__subtitle">
            <span className="helpbot-status-dot" /> Online
          </div>
        </div>
        <div className="helpbot-header__actions">
          <Tooltip content="New conversation" position="bottom">
            <button type="button" className="helpbot-icon-btn" onClick={bot.newConversation} aria-label="Start new conversation">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </Tooltip>
          {!isMobile && (
            <Tooltip content={bot.isMinimized ? 'Expand' : 'Minimise'} position="bottom">
              <button
                type="button"
                className="helpbot-icon-btn"
                onClick={bot.isMinimized ? bot.open : bot.minimize}
                aria-label={bot.isMinimized ? 'Expand Help Bot' : 'Minimise Help Bot'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={bot.isMinimized ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
                </svg>
              </button>
            </Tooltip>
          )}
          {!isMobile && (
            <Tooltip content="Close" position="bottom">
              <button type="button" className="helpbot-icon-btn" onClick={bot.close} aria-label="Close Help Bot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {!bot.isMinimized && (
        <>
          <div className="helpbot-body" ref={bodyRef} aria-live="polite">
            {!hasMessages && (
              <div className="helpbot-welcome">
                <div className="helpbot-welcome__emoji" aria-hidden="true">
                  👋
                </div>
                <h3>Hi! How can I help with NaijaNest?</h3>
                <p>Ask me about our features, guides, policies or safety information.</p>
                <div className="helpbot-chips">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button type="button" key={q} className="helpbot-chip" onClick={() => bot.sendMessage(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {bot.messages.map((message, index) => (
              <MessageBubble key={index} message={message} onActionClick={bot.close} />
            ))}

            {bot.isLoading && <TypingIndicator />}

            {bot.error && (
              <Alert type="error">
                {bot.error}
                <button type="button" className="helpbot-retry" onClick={bot.retry}>
                  Retry
                </button>
              </Alert>
            )}
          </div>

          <form
            className="helpbot-composer"
            onSubmit={(e) => {
              e.preventDefault();
              submitDraft();
            }}
          >
            <textarea
              ref={inputRef}
              className="helpbot-composer__input"
              placeholder="Ask a question…"
              rows={1}
              value={draft}
              disabled={bot.isLoading}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitDraft();
                }
              }}
              aria-label="Message NaijaNest Help"
            />
            <button type="submit" className="helpbot-send-btn" disabled={bot.isLoading || !draft.trim()} aria-label="Send message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </form>
        </>
      )}
    </div>
  );
}
